import { ReactElement, useCallback } from 'react';
import { DeepNonNullable } from 'ts-essentials';

import { VisitDischargeSummaryCreateCommand, VisitLocation } from '@dreryk/egabinet-visit-frontend-api';
import { DischargeSummaryApproveCommand } from '@dreryk/egabinet-discharge-summary-frontend-api';
import {
   Icd10Code,
   Icd10Entry,
   Icd10Name,
   Icd9Code,
   Icd9Entry,
   Icd9Name,
} from '@dreryk/egabinet-icd-dictionary-published-language-api';

import { useCqrsCommand, useDataSyncing } from '@dreryk/command-query-react-common';
import {
   ActionBar,
   ButtonVariant,
   DataSyncWaitingEventInterceptor,
   dispatchSnackbarPositiveEvent,
   Tooltip,
   usePushBack,
   ValidateFormEventInterceptor,
} from '@dreryk/react-components';
import { useDraftRemoveByTokenCqrsCommand } from '@dreryk/react-draft';
import { Choice } from '@dreryk/watermelon';
import { Translate } from '@dreryk/react-common';

import { visitPaths } from '~libs.desktop.shared.paths';
import { useGetCurrentHealthcareProviderType } from '~libs.shared.account';
import { PatientNfzDepartmentUpdateShowModalEventInterceptor } from '~libs.desktop.shared.patient';

import { dischargeSummaryTranslationKeys } from '../../translations';
import { getDischargeSummaryDraftToken } from '../dischargeSummary.utils';
import {
   DischargeSummaryFormBean,
   DischargeSummaryFormController,
} from '../DischargeSummaryForm/DischargeSummaryForm.types';
import { useVisitDocumentContext } from '../../../../common/visitDocument/VisitDocumentContext';

import { MedicalFieldVerificationShowModalEventInterceptor } from './medicalField/MedicalFieldVerificationShowModalEventInterceptor';

type DischargeSummarySendAndPrintActionParameters = DischargeSummaryFormBean & { externalId: string };

type DischargeSummarySendAndPrintActionProps = {
   readonly handleSubmit: DischargeSummaryFormController['handleSubmit'];
   readonly trigger: DischargeSummaryFormController['trigger'];
   readonly visitId: number;
   readonly patientId: number;
   readonly onSuccess: VoidFunction;
   readonly visitLocation: VisitLocation;
   readonly disabled: boolean;
};

// eslint-disable-next-line max-lines-per-function
export const DischargeSummarySendAndPrintAction = (props: DischargeSummarySendAndPrintActionProps): ReactElement => {
   const { executeVisitDocumentCommand } = useVisitDocumentContext();
   const {
      visitLocation: { serviceProvidingLocationId, healthcareFacilityLocationId, healthcareFacilityUnitId },
   } = props;
   const push = usePushBack();
   const healthcareProviderType = useGetCurrentHealthcareProviderType();
   const dataSyncing = useDataSyncing();

   const { execute: removeDischargeSummaryDraft } = useDraftRemoveByTokenCqrsCommand();
   const { executeAsync: sendAndPrintDischargeSummary } = useCqrsCommand<
      void,
      DischargeSummarySendAndPrintActionParameters
   >(
      (parameters) => {
         const validatedParameters = parameters as DischargeSummaryFormBean &
            DeepNonNullable<
               Pick<
                  DischargeSummaryFormBean,
                  | 'diagnosis'
                  | 'procedurePerformed'
                  | 'furtherMedicalTreatmentGuidelines'
                  | 'providedMedicalServiceDescription'
               >
            >;
         const command = new DischargeSummaryApproveCommand();
         command.externalId = parameters.externalId;
         command.diagnosis = Icd10Entry.of(
            Icd10Code.of(validatedParameters.diagnosis.code),
            Icd10Name.of(validatedParameters.diagnosis.name)
         );
         command.procedurePerformed = Icd9Entry.of(
            Icd9Code.of(validatedParameters.procedurePerformed.code),
            Icd9Name.of(validatedParameters.procedurePerformed.name)
         );
         command.medicalServiceDescription = validatedParameters.providedMedicalServiceDescription;
         command.medicalTreatmentIndications = validatedParameters.furtherMedicalTreatmentGuidelines;
         command.specialtyId = Choice.withUseDefaultIfNull(validatedParameters.specialty?.id ?? null);
         command.diagnosticsIndications = Choice.withUseDefaultIfNull(
            validatedParameters.diagnosticsGuidelines ?? null
         );
         command.pharmacotherapyIndications = Choice.withUseDefaultIfNull(
            validatedParameters.pharmacotherapyGuidelines ?? null
         );
         command.otherIndications = Choice.withUseDefaultIfNull(validatedParameters.otherGuidelines ?? null);
         command.medicalFieldCode = Choice.withUseDefaultIfNull(validatedParameters.medicalDepartment?.code ?? null);

         return command;
      },
      {
         onSuccess: () => {
            props.onSuccess();
            removeDischargeSummaryDraft({
               token: getDischargeSummaryDraftToken(props.visitId),
            });
            dispatchSnackbarPositiveEvent(
               dischargeSummaryTranslationKeys.documentHasBeenPreparedAndWillBeSendAfterCompletingVisit
            );
            push(
               visitPaths.getVisitInterviewPath({
                  params: {
                     visitId: props.visitId,
                  },
               })
            );
         },
      }
   );

   const locationIdMissing = !serviceProvidingLocationId && !healthcareFacilityLocationId && !healthcareFacilityUnitId;

   const handleSendAndPrintAction = useCallback(
      async (formData: DischargeSummaryFormBean) => {
         return executeVisitDocumentCommand(VisitDischargeSummaryCreateCommand, async (externalId) =>
            sendAndPrintDischargeSummary({ ...formData, externalId: externalId })
         );
      },
      [executeVisitDocumentCommand, sendAndPrintDischargeSummary]
   );

   if (healthcareProviderType === 'HEALTHCARE_ORGANIZATION') {
      return (
         <DataSyncWaitingEventInterceptor waitUntil={dataSyncing}>
            {(waiting) => (
               <PatientNfzDepartmentUpdateShowModalEventInterceptor
                  patientId={props.patientId}
                  modalLabel={dischargeSummaryTranslationKeys.missingNFZDepartment}
               >
                  <ActionBar.PrimaryButton
                     testId={'dischargeSummaryCreate'}
                     label={
                        <Tooltip
                           testId={'dischargeSummarySendAndPrint'}
                           content={dischargeSummaryTranslationKeys.cannotIssueOnReopenedVisit}
                           disabled={!props.disabled}
                        >
                           <Translate>{dischargeSummaryTranslationKeys.sendAndPrint}</Translate>
                        </Tooltip>
                     }
                     onClick={props.handleSubmit(handleSendAndPrintAction)}
                     variant={ButtonVariant.PRIMARY}
                     blockingOperation={dischargeSummaryTranslationKeys.preparingDocument}
                     disabled={props.disabled || locationIdMissing || waiting}
                  />
               </PatientNfzDepartmentUpdateShowModalEventInterceptor>
            )}
         </DataSyncWaitingEventInterceptor>
      );
   }

   return (
      <ValidateFormEventInterceptor trigger={props.trigger}>
         <MedicalFieldVerificationShowModalEventInterceptor serviceProvidingLocationId={serviceProvidingLocationId!}>
            <DataSyncWaitingEventInterceptor waitUntil={dataSyncing}>
               {(waiting) => (
                  <PatientNfzDepartmentUpdateShowModalEventInterceptor
                     patientId={props.patientId}
                     modalLabel={dischargeSummaryTranslationKeys.missingNFZDepartment}
                  >
                     <ActionBar.PrimaryButton
                        testId={'dischargeSummaryCreate'}
                        label={
                           <Tooltip
                              testId={'dischargeSummarySendAndPrint'}
                              content={dischargeSummaryTranslationKeys.cannotIssueOnReopenedVisit}
                              disabled={!props.disabled}
                           >
                              <Translate>{dischargeSummaryTranslationKeys.sendAndPrint}</Translate>
                           </Tooltip>
                        }
                        onClick={props.handleSubmit(handleSendAndPrintAction)}
                        variant={ButtonVariant.PRIMARY}
                        blockingOperation={dischargeSummaryTranslationKeys.preparingDocument}
                        disabled={props.disabled || locationIdMissing || waiting}
                     />
                  </PatientNfzDepartmentUpdateShowModalEventInterceptor>
               )}
            </DataSyncWaitingEventInterceptor>
         </MedicalFieldVerificationShowModalEventInterceptor>
      </ValidateFormEventInterceptor>
   );
};
